import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { View, TouchableOpacity } from "react-native";
import { AppText, AppIcon } from "@toothfairy/shared-ui";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppUser from "@toothfairy/shared-ui/AppUser";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import RechargeSheet from "../../../../Components/Credits/RechargeSheet";
import useCredits from "../../../../Hooks/useCredits";
import { formatUoI, formatUsd, MIN_RECHARGE_USD } from "../../../../Constants/billing";
import { useHistory } from "../../../../Router";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import AppToast from "@toothfairy/shared-ui/AppToast";
const formatNumber = (num) => {
  return num?.toLocaleString("en-US") || "0";
};

const PricingCard = ({
  subscription,
  isSelected,
  onSelect,
  currentSeats = 1,
  onSeatsChange,
  isUpdate = false,
  disabled = false,
  isCurrentPlan = false,
  currentPlan,
  balance,
  customUoiPrice = null,
  enterpriseMinUoI = null,
  minSeatsByConsumption = 0,
  totalUoIConsumed = 0,
  reservedComputeAllocation = false,
}) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const [seats, setSeats] = useState(currentSeats);
  const [totalPrice, setTotalPrice] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [showUoIInfo, setShowUoIInfo] = useState(false);
  const AppToasty = AppToast.useToast();
  const { user } = AppUser.useUser();
  const history = useHistory();
  const creditBalance =
    (balance ?? user?.stripeCustomerData?.balance ?? 0) / -100;
  const hasPaymentMethod =
    user?.stripeCustomerData?.invoice_settings?.default_payment_method;
  const hasBillingAddress =
    user?.stripeCustomerData?.address !== null &&
    user?.stripeCustomerData?.address?.line1 &&
    user?.stripeCustomerData?.address?.city &&
    user?.stripeCustomerData?.address?.country;
  const isBillingMissing = !hasPaymentMethod || !hasBillingAddress;
  const { getActiveWorkspace } = useWorkspaces();
  const initialSeatsRef = useRef(null);
  const initialPlanRef = useRef(null);
  const hasInitializedRef = useRef(false);

  const isPopular = subscription.type === "business";
  const isEnterprise = subscription.type === "enterprise";
  const isUoIModel = subscription.pricingModel === "perUoI";
  const isConsumptionModel = subscription.pricingModel === "consumption";
  const creditsHook = useCredits();
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const hasUoIPerUnit = subscription.uoiPerUnit > 0;
  const actualUoI = hasUoIPerUnit ? seats * subscription.uoiPerUnit : seats;

  const effectivePrice =
    isEnterprise && customUoiPrice ? customUoiPrice : subscription.price;
  const effectiveMinSeats =
    isEnterprise && enterpriseMinUoI
      ? Math.max(
          enterpriseMinUoI,
          subscription.minimumSeats || subscription.initialSeats,
        )
      : subscription.minimumSeats || subscription.initialSeats;

  useEffect(() => {
    if (
      subscription.pricingModel === "perSeat" ||
      subscription.pricingModel === "perUoI"
    ) {
      setTotalPrice(effectivePrice * seats);
    }
  }, [seats, effectivePrice, subscription.pricingModel]);
  useEffect(() => {
    setSeats(currentSeats);
  }, [currentSeats]);

  useEffect(() => {
    if (isUpdate && !hasInitializedRef.current) {
      initialSeatsRef.current = currentSeats;
      initialPlanRef.current = currentPlan;
      hasInitializedRef.current = true;
    }
    if (!isUpdate) {
      hasInitializedRef.current = false;
    }
  }, [isUpdate, currentSeats, currentPlan]);

  const handleSeatsChange = (newSeats) => {
    setSeats(newSeats);
    if (onSeatsChange) {
      onSeatsChange(newSeats, subscription.type);
    }
  };

  const getSubscriptionLevel = (subscriptionType) => {
    switch (subscriptionType) {
      case "base":
        return 1;
      case "pro":
        return 2;
      case "business":
        return 3;
      case "enterprise":
        return 4;
      default:
        return 0;
    }
  };

  const isUpgrade =
    currentPlan &&
    getSubscriptionLevel(subscription.type) > getSubscriptionLevel(currentPlan);
  const isDowngrade =
    currentPlan &&
    getSubscriptionLevel(subscription.type) < getSubscriptionLevel(currentPlan);

  const isEnterpriseCurrentPlan = isEnterprise && isCurrentPlan && isUpdate;
  const isBusinessCurrentPlan =
    subscription.type === "business" && isCurrentPlan && isUpdate;
  const isReservedCompute =
    reservedComputeAllocation &&
    isUpdate &&
    (isEnterpriseCurrentPlan || isBusinessCurrentPlan);
  const isUpdateActionDisabled = isReservedCompute;

  const hasChanges = useMemo(() => {
    if (!isUpdate) return true;
    const planChanged = subscription.type !== initialPlanRef.current;
    const seatsChanged = seats !== initialSeatsRef.current;
    if (isEnterpriseCurrentPlan) {
      return seatsChanged && seats > (initialSeatsRef.current || 0);
    }
    return planChanged || seatsChanged;
  }, [isUpdate, subscription.type, seats, isEnterpriseCurrentPlan]);

  const isButtonDisabled = useMemo(() => {
    if (isUpdateActionDisabled) {
      return true;
    }
    if (isUpdate && isCurrentPlan && !isUoIModel) {
      return true;
    }
    if (isEnterpriseCurrentPlan) {
      return disabled || !hasChanges;
    }
    return disabled || !hasChanges || isDowngrade;
  }, [
    disabled,
    hasChanges,
    isDowngrade,
    isUpdate,
    isCurrentPlan,
    isUoIModel,
    isEnterpriseCurrentPlan,
    isUpdateActionDisabled,
  ]);

  const showStepper = isUoIModel && (!isEnterprise || isEnterpriseCurrentPlan);

  return (
    <TouchableOpacity
      disabled={disabled}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        backgroundColor: currentTheme?.theme?.backgroundColor,
        borderRadius: 12,
        borderWidth: isSelected || isHovered ? 1 : 1,
        borderColor:
          isSelected || isHovered
            ? currentTheme?.theme?.primary
            : currentTheme?.theme?.borderColor || "#E0E0E0",
        padding: 24,
        margin: 8,
        minWidth: 300,
        maxWidth: 380,
        position: "relative",
        opacity: disabled ? 0.6 : 1,
        transition: "border-color 0.2s ease",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {isPopular && !isCurrentPlan && currentPlan !== "enterprise" && (
        <View
          style={{
            position: "absolute",
            top: -12,
            left: 20,
            right: 20,
            backgroundColor: currentTheme?.theme?.primary || "#007AFF",
            borderRadius: 20,
            paddingVertical: 6,
            paddingHorizontal: 16,
            alignItems: "center",
            zIndex: 1,
          }}
        >
          <AppText
            style={{
              color: "white",
              fontSize: 12,
              fontWeight: "bold",
            }}
          >
            {t("pricing.mostPopular")}
          </AppText>
        </View>
      )}

      {isCurrentPlan && (
        <View
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            backgroundColor: currentTheme?.theme?.orange || "#4CAF50",
            borderRadius: 12,
            paddingVertical: 4,
            paddingHorizontal: 8,
            alignItems: "center",
            zIndex: 1,
          }}
        >
          <AppText
            style={{
              color: "white",
              fontSize: 10,
              fontWeight: "bold",
            }}
          >
            {t("pricing.currentPlan")}
          </AppText>
        </View>
      )}

      <View
        style={{
          flex: 1,
          flexDirection: "column",
        }}
      >
        <View
          style={{
            alignItems: "center",
            marginBottom: 20,
            marginTop: isPopular ? 0 : 0,
          }}
        >
          <AppText
            style={{
              fontSize: 24,
              fontWeight: "bold",
              color: currentTheme?.theme?.font_color,
              marginBottom: 8,
            }}
          >
            {t(subscription.titleKey || subscription.title)}
          </AppText>

          <View
            style={{
              flexDirection: "row",
              alignItems: "baseline",
              marginBottom: 8,
            }}
          >
            {isEnterprise && !isEnterpriseCurrentPlan ? null : isConsumptionModel ? (
              <>
                <AppText
                  style={{
                    fontSize: 14,
                    color: currentTheme?.theme?.mediumGrey,
                    fontWeight: "bold",
                    marginRight: 4,
                  }}
                >
                  {t("pricing.from")}
                </AppText>
                <AppText
                  preventTranslation
                  style={{
                    fontSize: 32,
                    fontWeight: "bold",
                    color: currentTheme?.theme?.primary || "#007AFF",
                  }}
                >
                  {subscription.currency}
                  {MIN_RECHARGE_USD.toFixed(2)}
                </AppText>
                <AppText
                  style={{
                    fontSize: 14,
                    color:
                      currentTheme?.theme?.secondary_font_color ||
                      currentTheme?.theme?.font_color,
                    marginLeft: 4,
                  }}
                >
                  /{t("pricing.recharge")}
                </AppText>
              </>
            ) : (
              <>
                {isUoIModel && (
                  <AppText
                    style={{
                      fontSize: 14,
                      color: currentTheme?.theme?.mediumGrey,
                      fontWeight: "bold",
                      marginRight: 4,
                    }}
                  >
                    {t("pricing.from")}
                  </AppText>
                )}
                <AppText
                  preventTranslation
                  style={{
                    fontSize: 32,
                    fontWeight: "bold",
                    color: currentTheme?.theme?.primary || "#007AFF",
                  }}
                >
                  {subscription.currency}
                  {isUoIModel
                    ? (effectiveMinSeats * effectivePrice).toFixed(2)
                    : subscription.price.toFixed(2)}
                </AppText>
                {isUoIModel && (
                  <AppText
                    style={{
                      fontSize: 14,
                      color:
                        currentTheme?.theme?.secondary_font_color ||
                        currentTheme?.theme?.font_color,
                      marginLeft: 4,
                    }}
                  >
                    /{t(subscription.unitKey || "pricing.units.perUoi")}
                  </AppText>
                )}
                {!isUoIModel && subscription.unit && (
                  <AppText
                    style={{
                      fontSize: 14,
                      color:
                        currentTheme?.theme?.secondary_font_color ||
                        currentTheme?.theme?.font_color,
                      marginLeft: 4,
                    }}
                  >
                    /{t(subscription.unitKey || subscription.unit)}
                  </AppText>
                )}
              </>
            )}
          </View>

          {isEnterprise &&
            isEnterpriseCurrentPlan &&
            customUoiPrice &&
            customUoiPrice !== 0.01 && (
              <AppText
                style={{
                  fontSize: 11,
                  color: currentTheme?.theme?.green || "#4CAF50",
                  fontStyle: "italic",
                  marginBottom: 4,
                }}
              >
                {t("pricing.enterpriseCustomRate", { rate: customUoiPrice })}
              </AppText>
            )}

        </View>

        <AppText
          style={{
            fontSize: 12,
            color: currentTheme?.theme?.font_color,
            textAlign: "center",
            marginBottom: 20,
            lineHeight: 20,
            fontWeight: "bold",
          }}
        >
          {t(subscription.descriptionKey || subscription.description)}
        </AppText>

        {showStepper && (
          <View style={{ marginBottom: 20 }}>
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                marginBottom: 8,
                gap: 6,
              }}
            >
              <AppText
                style={{
                  fontSize: 14,
                  fontWeight: "bold",
                  color: currentTheme?.theme?.font_color,
                }}
              >
                {isUoIModel
                  ? t("pricing.unitsOfIntelligence", {
                      count: formatNumber(actualUoI),
                    })
                  : t("pricing.numberOfSeats", { count: seats })}
              </AppText>
              {isUoIModel && (
                <View style={{ position: "relative" }}>
                  <TouchableOpacity
                    onPress={() => setShowUoIInfo(!showUoIInfo)}
                    onMouseEnter={() => setShowUoIInfo(true)}
                    onMouseLeave={() => setShowUoIInfo(false)}
                  >
                    <AppIcon
                      icon={AppAssets?.icons?.info}
                      color={currentTheme?.theme?.primary}
                      size={14}
                    />
                  </TouchableOpacity>
                  {showUoIInfo && (
                    <View
                      style={{
                        position: "absolute",
                        bottom: 24,
                        left: -100,
                        width: 250,
                        backgroundColor:
                          currentTheme?.theme?.tooltipBackgroundColor ||
                          currentTheme?.theme?.softBackgroundColor,
                        padding: 12,
                        borderRadius: 8,
                        zIndex: 1000,
                        shadowColor: "#000",
                        shadowOffset: { width: 0, height: -2 },
                        shadowOpacity: 0.25,
                        shadowRadius: 3.84,
                        elevation: 5,
                      }}
                    >
                      <AppText
                        style={{
                          fontSize: 12,
                          color: "#FFFFFF",
                          lineHeight: 18,
                        }}
                      >
                        {isEnterprise
                          ? t("pricing.uoiTooltipEnterprise")
                          : t("pricing.uoiTooltip")}
                      </AppText>
                    </View>
                  )}
                </View>
              )}
            </View>

            {isEnterprise && isEnterpriseCurrentPlan && (
              <AppText
                style={{
                  fontSize: 11,
                  color: currentTheme?.theme?.orange,
                  marginBottom: 8,
                  fontStyle: "italic",
                }}
              >
                {t("pricing.enterpriseMinCommitment", {
                  count: formatNumber(effectiveMinSeats),
                })}
              </AppText>
            )}

            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <AppIcon
                onPress={() => {
                  if (isReservedCompute) {
                    AppToasty.show(
                      t("pricing.reservedComputeAllocationToast"),
                      { type: "danger" },
                    );
                    return;
                  }
                  const stepSize = subscription.stepSize || 1;
                  const newSeats = seats - stepSize;
                  if (newSeats < minSeatsByConsumption) {
                    AppToasty.show(
                      t("pricing.cannotReduceBelowConsumption", {
                        consumed: formatNumber(Math.ceil(totalUoIConsumed)),
                      }),
                      { type: "danger" },
                    );
                  } else if (
                    isUpdate &&
                    !isUoIModel &&
                    newSeats < getActiveWorkspace()?.users?.length
                  ) {
                    AppToasty.show(
                      t("pricing.cannotReduceSeats", {
                        count: getActiveWorkspace()?.users?.length,
                      }),
                      { type: "danger" },
                    );
                  } else {
                    handleSeatsChange(Math.max(effectiveMinSeats, newSeats));
                  }
                }}
                icon={AppAssets?.icons?.minus}
                color={
                  seats <= effectiveMinSeats || isDowngrade || isReservedCompute
                    ? currentTheme?.theme?.mediumGrey
                    : currentTheme?.theme?.primary
                }
                isClickable={
                  !(seats <= effectiveMinSeats || isDowngrade || isReservedCompute)
                }
              />

              <AppText
                style={{
                  fontSize: 18,
                  fontWeight: "bold",
                  color: currentTheme?.theme?.font_color,
                  marginHorizontal: 20,
                }}
                preventTranslation
              >
                {isUoIModel ? formatNumber(actualUoI) : seats}
              </AppText>
              <AppIcon
                onPress={() => {
                  if (isReservedCompute) {
                    AppToasty.show(
                      t("pricing.reservedComputeAllocationToast"),
                      { type: "danger" },
                    );
                    return;
                  }
                  const stepSize = subscription.stepSize || 1;
                  handleSeatsChange(
                    Math.min(subscription.maxSeats, seats + stepSize),
                  );
                }}
                icon={AppAssets?.icons?.plusCircle}
                color={
                  seats >= subscription.maxSeats ||
                  isDowngrade ||
                  isReservedCompute
                    ? currentTheme?.theme?.mediumGrey
                    : currentTheme?.theme?.primary
                }
                isClickable={
                  !(
                    seats >= subscription.maxSeats ||
                    isDowngrade ||
                    isReservedCompute
                  )
                }
              />
            </View>

            <AppText
              style={{
                fontSize: 16,
                fontWeight: "bold",
                color: currentTheme?.theme?.primary,
                textAlign: "center",
                marginTop: 12,
              }}
            >
              {t("pricing.totalPerMonth", {
                currency: subscription.currency,
                price: totalPrice.toFixed(2),
              })}
            </AppText>
            {isUpdate &&
              minSeatsByConsumption > 0 &&
              seats <= minSeatsByConsumption && (
                <AppText
                  style={{
                    fontSize: 11,
                    color: currentTheme?.theme?.orange,
                    textAlign: "center",
                    marginTop: 8,
                    fontStyle: "italic",
                  }}
                >
                  {t("pricing.consumptionFloorMessage", {
                    consumed: formatNumber(Math.ceil(totalUoIConsumed)),
                  })}
                </AppText>
              )}
            {isReservedCompute && (
              <AppText
                style={{
                  fontSize: 11,
                  color: currentTheme?.theme?.orange,
                  textAlign: "center",
                  marginTop: 8,
                  fontStyle: "italic",
                }}
              >
                {t("pricing.reservedComputeAllocationToast")}
              </AppText>
            )}
          </View>
        )}

        <View
          style={{
            borderTopWidth: 1,
            borderTopColor: currentTheme?.theme?.borderColor || "#E0E0E0",
            paddingTop: 20,
          }}
        >
          <AppText
            style={{
              fontSize: 16,
              fontWeight: "bold",
              color: currentTheme?.theme?.font_color,
              marginBottom: 12,
            }}
          >
            {t("pricing.whatsIncluded")}
          </AppText>

          {subscription.pricingLeafs.map((feature, index) => (
            <View
              key={index}
              style={{
                flexDirection: "row",
                alignItems: "flex-start",
                marginBottom: 8,
              }}
            >
              <AppText
                iconSize={12}
                leftIcon={AppAssets?.icons?.tick}
                style={{
                  marginLeft: 10,
                  fontSize: 14,
                  color: currentTheme?.theme?.font_color,
                  flex: 1,
                  fontWeight: feature.bold ? "bold" : "normal",
                }}
              >
                {t(feature.text, feature.params || {})}
              </AppText>
            </View>
          ))}
        </View>
      </View>

      <View>
        <ToothFairyButton
          title={
            isConsumptionModel
              ? t(subscription.ctaLabelKey || subscription.ctaLabel)
              : isEnterprise && !isEnterpriseCurrentPlan
              ? t(subscription.ctaLabelKey || subscription.ctaLabel)
              : isEnterprise && isEnterpriseCurrentPlan
              ? t("workspaces.pricing.update")
              : totalPrice <= creditBalance
              ? t("workspaces.pricing.freeWithCredits")
              : isBillingMissing &&
                !isConsumptionModel &&
                subscription.type !== "enterprise"
              ? t("workspaces.pricing.setupBilling")
              : isUpdate && !isCurrentPlan
              ? isUpgrade
                ? t("workspaces.pricing.upgrade")
                : isDowngrade
                ? t("workspaces.pricing.notAvailable")
                : t("workspaces.pricing.update")
              : isUpdate && isCurrentPlan
              ? isUoIModel
                ? t("workspaces.pricing.update")
                : t("workspaces.pricing.currentPlan")
              : t(subscription.ctaLabelKey || subscription.ctaLabel)
          }
          onPress={() => {
            if (isConsumptionModel) {
              onSelect(subscription);
              setRechargeOpen(true);
            } else if (isReservedCompute) {
              AppToasty.show(
                t("pricing.reservedComputeAllocationToast"),
                { type: "danger" },
              );
            } else if (seats < minSeatsByConsumption) {
              AppToasty.show(
                t("pricing.cannotReduceBelowConsumption", {
                  consumed: formatNumber(Math.ceil(totalUoIConsumed)),
                }),
                { type: "danger" },
              );
            } else if (
              isBillingMissing &&
              totalPrice > creditBalance &&
              subscription.type !== "enterprise"
            ) {
              // Consumption model recharges one-off via Stripe Checkout with any
              // card, so a saved payment method is NOT required to top up. Only
              // push to billing setup for the legacy subscription path.
              if (isConsumptionModel) {
                setRechargeOpen(true);
              } else {
                history.push("/profile/billing");
              }
            } else {
              onSelect(subscription);
            }
          }}
          isDisabled={isButtonDisabled}
          is_secondary={!isSelected}
          style={{
            marginTop: 20,
            borderRadius: 8,
            marginHorizontal: 0,
          }}
          wrapperStyle={{
            alignSelf: "center",
          }}
        />

        {isUpdate && isDowngrade && (
          <AppText
            style={{
              fontSize: 12,
              color:
                currentTheme?.theme?.secondary_font_color ||
                currentTheme?.theme?.font_color,
              textAlign: "center",
              marginTop: 8,
              fontStyle: "italic",
            }}
          >
            {t("pricing.downgradeMessage")}
          </AppText>
        )}

        {isEnterprise && isEnterpriseCurrentPlan && seats > effectiveMinSeats && (
          <AppText
            style={{
              fontSize: 11,
              color: currentTheme?.theme?.orange,
              textAlign: "center",
              marginTop: 8,
              fontStyle: "italic",
            }}
          >
            {t("pricing.enterpriseMaxSpendHint", {
              minUoI: formatNumber(effectiveMinSeats),
            })}
          </AppText>
        )}
      </View>

      {isConsumptionModel && (
        <RechargeSheet
          isModalVisible={rechargeOpen}
          closeModal={() => setRechargeOpen(false)}
          credits={creditsHook}
          titleKey="credits.recharge.rechargeAndStart"
          confirmKey="credits.recharge.rechargeAndStart"
          returnTo="/workspaces"
        />
      )}
    </TouchableOpacity>
  );
};

export default PricingCard;
